import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from 'components/ui/Sidebar';
import Icon from 'components/AppIcon';
import { useAuth } from 'contexts/AuthContext';
import { supabase } from 'lib/supabase';
import { ROLES } from 'utils/rbac';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-secondary-100 text-secondary-700' },
  invoiced: { label: 'Invoiced', color: 'bg-primary-50 text-primary' },
  paid: { label: 'Paid', color: 'bg-success-50 text-success-700' }
};

const TimeEntryManagement = () => {
  const { user, profile } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterCase, setFilterCase] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterBillable, setFilterBillable] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');

  const isAdminOrPartner = [ROLES?.PARTNER, ROLES?.ADMIN]?.includes(profile?.role);

  const fetchEntries = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      setError(null);
      let query = supabase?.from('time_entries')?.select('*, user_profiles!time_entries_lawyer_id_fkey(full_name, email)')?.order('created_at', { ascending: false });

      if (!isAdminOrPartner) {
        query = query?.eq('lawyer_id', user?.id);
      }
      if (filterCase) query = query?.ilike('case_name', `%${filterCase}%`);
      if (filterStatus) query = query?.eq('entry_status', filterStatus);
      if (filterBillable !== '') query = query?.eq('billable_flag', filterBillable === 'true');
      if (filterDateFrom) query = query?.gte('started_at', new Date(filterDateFrom)?.toISOString());
      if (filterDateTo) {
        const to = new Date(filterDateTo);
        to?.setDate(to?.getDate() + 1);
        query = query?.lt('started_at', to?.toISOString());
      }

      const { data, error: fetchError } = await query;
      if (fetchError) throw fetchError;
      setEntries(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load entries');
    } finally {
      setLoading(false);
    }
  }, [user?.id, isAdminOrPartner, filterCase, filterStatus, filterBillable, filterDateFrom, filterDateTo]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(entries?.filter(e => e?.entry_status === 'draft')?.map(e => e?.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id, checked) => {
    setSelectedIds(prev => checked ? [...prev, id] : prev?.filter(i => i !== id));
  };

  const handleBulkDelete = async () => {
    if (selectedIds?.length === 0) return;
    try {
      const { error: deleteError } = await supabase?.from('time_entries')?.delete()?.in('id', selectedIds)?.eq('entry_status', 'draft');
      if (deleteError) throw deleteError;
      setSuccessMessage(`${selectedIds?.length} entries deleted`);
      setSelectedIds([]);
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchEntries();
    } catch (err) {
      setError(err?.message || 'Failed to delete entries');
    }
  };

  const formatDuration = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr)?.toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const totalBillable = entries?.filter(e => e?.billable_flag)?.reduce((s, e) => s + Number(e?.billable_amount || 0), 0);
  const totalHours = entries?.reduce((s, e) => s + (e?.duration_minutes || 0), 0);
  const draftEntries = entries?.filter(e => e?.entry_status === 'draft');

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <Sidebar />
      <main className="flex-1 overflow-y-auto md:ml-sidebar">
        {/* Header */}
        <div className="bg-surface border-b border-border px-6 py-4 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-heading-semibold text-text-primary">Time Entry Management</h1>
              <p className="text-sm text-text-secondary mt-0.5">
                {isAdminOrPartner ? 'All firm time entries' : 'Your time entries'}
              </p>
            </div>
            <a
              href="/time-tracking-dashboard"
              className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm font-body-medium hover:bg-primary-700 transition-colors"
            >
              <Icon name="Timer" size={16} />
              Track Time
            </a>
          </div>
        </div>

        <div className="p-6">
          {/* Messages */}
          {successMessage && (
            <div className="mb-4 flex items-center gap-2 bg-success-50 text-success-700 border border-success-200 px-4 py-3 rounded-lg">
              <Icon name="CheckCircle" size={16} />
              <span className="text-sm">{successMessage}</span>
            </div>
          )}
          {error && (
            <div className="mb-4 flex items-center gap-2 bg-error-50 text-error-700 border border-error-200 px-4 py-3 rounded-lg">
              <Icon name="AlertCircle" size={16} />
              <span className="text-sm">{error}</span>
              <button onClick={() => setError(null)} className="ml-auto"><Icon name="X" size={14} /></button>
            </div>
          )}

          {/* Summary Row */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-surface rounded-xl border border-border p-4">
              <p className="text-sm text-text-secondary mb-1">Total Hours</p>
              <p className="text-xl font-heading-semibold text-text-primary">{formatDuration(totalHours)}</p>
            </div>
            <div className="bg-surface rounded-xl border border-border p-4">
              <p className="text-sm text-text-secondary mb-1">Total Billable</p>
              <p className="text-xl font-heading-semibold text-success-700">KES {totalBillable?.toLocaleString('en-KE', { minimumFractionDigits: 0 })}</p>
            </div>
            <div className="bg-surface rounded-xl border border-border p-4">
              <p className="text-sm text-text-secondary mb-1">Draft Entries</p>
              <p className="text-xl font-heading-semibold text-warning-700">{draftEntries?.length}</p>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-surface rounded-xl border border-border p-4 mb-5">
            <div className="flex flex-wrap gap-3">
              <div className="relative flex-1 min-w-48">
                <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                <input
                  type="text"
                  value={filterCase}
                  onChange={e => setFilterCase(e?.target?.value)}
                  placeholder="Filter by case..."
                  className="w-full pl-9 pr-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e?.target?.value)}
                className="px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface"
              >
                <option value="">All Status</option>
                <option value="draft">Draft</option>
                <option value="invoiced">Invoiced</option>
                <option value="paid">Paid</option>
              </select>
              <select
                value={filterBillable}
                onChange={e => setFilterBillable(e?.target?.value)}
                className="px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface"
              >
                <option value="">All Types</option>
                <option value="true">Billable</option>
                <option value="false">Non-Billable</option>
              </select>
              <input
                type="date"
                value={filterDateFrom}
                onChange={e => setFilterDateFrom(e?.target?.value)}
                className="px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="From"
              />
              <input
                type="date"
                value={filterDateTo}
                onChange={e => setFilterDateTo(e?.target?.value)}
                className="px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="To"
              />
              <button
                onClick={fetchEntries}
                className="flex items-center gap-1.5 px-3 py-2 border border-border rounded-lg text-sm text-text-secondary hover:bg-secondary-50 transition-colors"
              >
                <Icon name="RefreshCw" size={14} />
              </button>
            </div>
          </div>

          {/* Bulk Actions */}
          {selectedIds?.length > 0 && (
            <div className="flex items-center gap-3 mb-4 bg-primary-50 border border-primary-200 px-4 py-2.5 rounded-lg">
              <span className="text-sm font-body-medium text-primary">{selectedIds?.length} selected</span>
              <button
                onClick={handleBulkDelete}
                className="flex items-center gap-1.5 text-sm text-error-600 hover:text-error-700 font-body-medium"
              >
                <Icon name="Trash2" size={14} />
                Delete Selected
              </button>
              <button
                onClick={() => setSelectedIds([])}
                className="ml-auto text-sm text-text-secondary hover:text-text-primary"
              >
                Clear
              </button>
            </div>
          )}

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="flex items-center gap-3 text-text-secondary">
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                Loading...
              </div>
            </div>
          ) : entries?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 bg-surface rounded-2xl border border-border">
              <div className="w-16 h-16 bg-secondary-50 rounded-2xl flex items-center justify-center mb-4">
                <Icon name="ClipboardList" size={28} className="text-text-secondary" />
              </div>
              <p className="font-body-medium text-text-primary mb-1">No time entries found</p>
              <p className="text-sm text-text-secondary">Adjust filters or start tracking time</p>
            </div>
          ) : (
            <div className="bg-surface rounded-2xl border border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-secondary-50">
                      <th className="px-4 py-3 w-10">
                        <input
                          type="checkbox"
                          onChange={e => handleSelectAll(e?.target?.checked)}
                          checked={selectedIds?.length === draftEntries?.length && draftEntries?.length > 0}
                          className="rounded border-border"
                        />
                      </th>
                      {isAdminOrPartner && (
                        <th className="text-left px-4 py-3 text-xs font-body-semibold text-text-secondary uppercase tracking-wide">Lawyer</th>
                      )}
                      <th className="text-left px-4 py-3 text-xs font-body-semibold text-text-secondary uppercase tracking-wide">Date</th>
                      <th className="text-left px-4 py-3 text-xs font-body-semibold text-text-secondary uppercase tracking-wide">Activity</th>
                      <th className="text-left px-4 py-3 text-xs font-body-semibold text-text-secondary uppercase tracking-wide">Case</th>
                      <th className="text-center px-4 py-3 text-xs font-body-semibold text-text-secondary uppercase tracking-wide">Duration</th>
                      <th className="text-right px-4 py-3 text-xs font-body-semibold text-text-secondary uppercase tracking-wide">Amount</th>
                      <th className="text-center px-4 py-3 text-xs font-body-semibold text-text-secondary uppercase tracking-wide">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {entries?.map(entry => {
                      const statusCfg = STATUS_CONFIG?.[entry?.entry_status] || STATUS_CONFIG?.draft;
                      const isDraft = entry?.entry_status === 'draft';
                      return (
                        <tr key={entry?.id} className={`hover:bg-secondary-50 transition-colors ${selectedIds?.includes(entry?.id) ? 'bg-primary-50' : ''}`}>
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedIds?.includes(entry?.id)}
                              onChange={e => handleSelectOne(entry?.id, e?.target?.checked)}
                              disabled={!isDraft}
                              className="rounded border-border disabled:opacity-40"
                            />
                          </td>
                          {isAdminOrPartner && (
                            <td className="px-4 py-3">
                              <p className="text-sm text-text-primary">{entry?.user_profiles?.full_name || '—'}</p>
                              <p className="text-xs text-text-secondary">{entry?.user_profiles?.email}</p>
                            </td>
                          )}
                          <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                            {formatDate(entry?.started_at || entry?.created_at)}
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm text-text-primary line-clamp-2 max-w-xs">{entry?.activity_description}</p>
                          </td>
                          <td className="px-4 py-3">
                            {entry?.case_name ? (
                              <div>
                                <p className="text-sm text-text-primary">{entry?.case_name}</p>
                                {entry?.case_id && <p className="text-xs text-text-secondary">{entry?.case_id}</p>}
                              </div>
                            ) : <span className="text-sm text-text-secondary">—</span>}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-sm font-body-medium">{formatDuration(entry?.duration_minutes)}</span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {entry?.billable_flag ? (
                              <span className="text-sm font-body-semibold text-success-700">
                                KES {Number(entry?.billable_amount || 0)?.toLocaleString('en-KE', { minimumFractionDigits: 2 })}
                              </span>
                            ) : (
                              <span className="text-xs text-text-secondary">Non-billable</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-body-medium ${statusCfg?.color}`}>
                              {statusCfg?.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-3 border-t border-border bg-secondary-50 flex items-center justify-between">
                <span className="text-sm text-text-secondary">{entries?.length} entries</span>
                <span className="text-sm font-body-medium text-text-primary">
                  Total: KES {totalBillable?.toLocaleString('en-KE', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default TimeEntryManagement;
